import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  ButtonGroup,
  Chip,
  CircularProgress,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useMediaQuery,
  useTheme,
  Checkbox,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import AddCircleRoundedIcon from "@mui/icons-material/AddCircleRounded";
import CameraAltRoundedIcon from "@mui/icons-material/CameraAltRounded";
import VideocamRoundedIcon from "@mui/icons-material/VideocamRounded";
import VideocamOffRoundedIcon from "@mui/icons-material/VideocamOffRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import { useSession } from "../../context/SessionContext";
import { useCameraApi } from "../../api/useCameraApi";
import { Camera, CAMERA_TYPES, CameraTags } from "../../state/types";
import { useSystemsContext } from "../../context/SystemsContext";
import { rankForCameraTag } from "../carousel/cameraTagPosition";

// Dropdown order: Unassigned pinned first, then every other tag sorted by the
// canonical CCW rank (LFH -> LF -> LFL, LSH -> LS -> LSL, ...). Each location's
// High / base / Low variants stay grouped together so the picker reads as a
// walk around the vehicle.
const SORTED_CAMERA_TAGS: CameraTags[] = [
  CameraTags.Unassigned,
  ...Object.values(CameraTags)
    .filter((tag) => tag !== CameraTags.Unassigned)
    .sort((a, b) => rankForCameraTag(a) - rankForCameraTag(b)),
];

type CameraMode = "add" | "edit";
type HardwareTab = "cameras";
type CameraView = CAMERA_TYPES.EXTERIOR | CAMERA_TYPES.INTERIOR | "undercarriage";

const EMPTY_SNACK = { open: false, message: "", severity: "success" as "success" | "error" | "info" };

const THETA_USERNAME_PATTERN = /^THETA[A-Z]{2}\d{8}$/i;

const deriveThetaPassword = (rawUsername: string | undefined | null): string => {
  return (rawUsername || "").replace(/\D/g, "");
};

const isCameraInView = (camera: Camera, view: CameraView) => {
  if (view === CAMERA_TYPES.INTERIOR) {
    return camera.type === CAMERA_TYPES.INTERIOR;
  }

  if (view === "undercarriage") {
    return camera.type === CAMERA_TYPES.EXTERIOR && Boolean(camera.is_under_carriage);
  }

  return camera.type === CAMERA_TYPES.EXTERIOR && !camera.is_under_carriage;
};

const getCameraViewLabel = (view: CameraView) => {
  if (view === CAMERA_TYPES.INTERIOR) return "Interior";
  if (view === "undercarriage") return "Undercarriage";
  return "Exterior";
};

// The unit stores cameraId as its Django primary key (a 64-bit BigAutoField),
// so a new camera's id must be numeric — a UUID gets skipped by the unit's
// system sync. A millisecond timestamp plus a small random suffix stays well
// above the unit's small auto-increment ids (no collisions) and inside JS's
// safe-integer range. Kept as a numeric string to match the table's string key.
const generateCameraId = (): string =>
  String(Date.now() * 1000 + Math.floor(Math.random() * 1000));

const createDraftCamera = (view: CameraView, cameras: Camera[]): Camera => {
  const type = view === CAMERA_TYPES.INTERIOR ? CAMERA_TYPES.INTERIOR : CAMERA_TYPES.EXTERIOR;
  const lastExteriorPosition = cameras
    .filter((camera) => camera.type === CAMERA_TYPES.EXTERIOR)
    .reduce((max, camera) => Math.max(max, Number(camera.camera_position) || 0), 0);

  return {
    cameraId: generateCameraId(),
    type,
    brand: type === CAMERA_TYPES.EXTERIOR ? "axis" : "theta",
    camera_position: type === CAMERA_TYPES.EXTERIOR ? lastExteriorPosition + 1 : 0,
    ip_address: "",
    username: "",
    password: "",
    name: "",
    query_params: "",
    order: cameras.filter((camera) => camera.type === type).length + 1,
    tag: type === CAMERA_TYPES.EXTERIOR ? CameraTags.Unassigned : "",
    skip_ricoh_while_charging: false,
    is_under_carriage: view === "undercarriage",
    is_autoscan: false,
  };
};

const getCameraId = (camera: Camera) => String(camera.cameraId ?? camera.id ?? "");

const sortCameras = (cameras: Camera[]) =>
  [...cameras].sort((left, right) => {
    if (left.type !== right.type) {
      return left.type === CAMERA_TYPES.EXTERIOR ? -1 : 1;
    }

    if (left.type === CAMERA_TYPES.EXTERIOR) {
      return (left.camera_position || 0) - (right.camera_position || 0);
    }

    const leftOrder = left.order || 0;
    const rightOrder = right.order || 0;
    if (leftOrder !== rightOrder) {
      return leftOrder - rightOrder;
    }

    return (left.name || "").localeCompare(right.name || "");
  });

const normalizeExteriorPositions = (cameras: Camera[]) => {
  // camera_position is derived from the tag using the canonical CCW order
  // (LFH=1, LF=1-if-no-LFH, etc.). Stable .sort preserves the input order on
  // rank ties, which keeps undercarriage / unassigned cameras grouped at the end.
  const exterior = cameras
    .filter((camera) => camera.type === CAMERA_TYPES.EXTERIOR)
    .sort((left, right) => rankForCameraTag(left.tag) - rankForCameraTag(right.tag))
    .map((camera, index) => ({
      ...camera,
      camera_position: index + 1,
      order: index + 1,
    }));

  const interior = cameras
    .filter((camera) => camera.type === CAMERA_TYPES.INTERIOR)
    .sort((left, right) => (left.order || 0) - (right.order || 0))
    .map((camera, index) => ({
      ...camera,
      order: index + 1,
    }));

  return sortCameras([...exterior, ...interior]);
};

const normalizeCamera = (camera: Camera): Camera => {
  const resolvedType = camera.type ?? camera.camera_type ?? CAMERA_TYPES.EXTERIOR;
  const resolvedIpAddress = camera.ip_address || camera.http_config?.ip_address || "";
  const resolvedQueryParams = camera.query_params ?? camera.http_config?.query_params ?? "";
  const resolvedUsername = camera.username || camera.http_config?.username || "";
  const resolvedPassword = camera.password || camera.http_config?.password || "";
  const resolvedSkipRicoh = Boolean(
    camera.skip_ricoh_while_charging ?? camera.desired_settings?.skip_ricoh_while_charging
  );

  return {
    ...camera,
    cameraId: camera.cameraId ?? camera.id,
    id: camera.id ?? camera.cameraId,
    type: resolvedType,
    camera_type: resolvedType,
    brand: camera.brand || (resolvedType === CAMERA_TYPES.INTERIOR ? "theta" : "axis"),
    capture_protocol: camera.capture_protocol || (camera.brand?.toLowerCase() === "axis" ? "http" : "mqtt"),
    camera_position: Number(camera.camera_position) || 0,
    order: Number(camera.order ?? camera.sort_position) || Number(camera.camera_position) || 0,
    sort_position: Number(camera.sort_position ?? camera.order ?? camera.camera_position) || 0,
    ip_address: resolvedIpAddress,
    username: resolvedUsername,
    password: resolvedPassword,
    name: camera.name || "",
    query_params: resolvedQueryParams,
    tag: camera.tag || (resolvedType === CAMERA_TYPES.EXTERIOR ? CameraTags.Unassigned : ""),
    skip_ricoh_while_charging: resolvedSkipRicoh,
    is_under_carriage: Boolean(camera.is_under_carriage),
    is_autoscan: Boolean(camera.is_autoscan),
    http_config: {
      ...(camera.http_config || {}),
      ip_address: resolvedIpAddress,
      username: resolvedUsername,
      password: resolvedPassword,
      query_params: resolvedQueryParams,
    },
    desired_settings: {
      ...(camera.desired_settings || {}),
      skip_ricoh_while_charging: resolvedSkipRicoh,
    },
  };
};

const cameraSubtitle = (camera: Camera) => {
  if (camera.is_under_carriage) {
    return camera.tag || "Undercarriage";
  }

  if (camera.type === CAMERA_TYPES.EXTERIOR) {
    return camera.tag || CameraTags.Unassigned;
  }

  return camera.name || camera.username || "Interior camera";
};

const getAssignedConfigNames = (camera: Camera, configurations: Record<string, any>) => {
  const cameraId = getCameraId(camera);

  return Object.entries(configurations || {})
    .filter(([, config]) => {
      const cameraIds = config?.clientProcess?.cameras ?? config?.cameras ?? [];
      return cameraIds.map(String).includes(cameraId);
    })
    .map(([name]) => name);
};

// Camera-facing web UIs live at the host root and are reachable only over the
// bwicontrol.com VPN: BWICam serves its tuning/setup/calibration/capture landing
// page there, and Axis cameras serve their own web UI. Theta interior cameras
// have no such page, so we only offer this for axis/bwi.
const isCameraUiAvailable = (camera: Camera | null) => {
  const brand = camera?.brand?.toLowerCase();
  return brand === "bwi" || brand === "axis";
};

const getCameraUiUrl = (camera: Camera | null, systemCode?: string) => {
  if (!camera?.ip_address || !systemCode) {
    return "";
  }
  const hostIp = camera.ip_address.replace(/\./g, "-");
  return `http://${hostIp}.${systemCode}.bwicontrol.com/`;
};

const openCameraUiTab = (url: string) => {
  // No window features → a real tab, not a popup. Sever opener so the camera
  // page can't script back into the app.
  const tab = window.open(url, "_blank");
  if (tab) tab.opener = null;
};

// Best-effort reachability check — resolves true if the host answers. This can
// only actually run when the app itself is served over http; from an https page
// the browser blocks the request to the http-only camera (mixed content) and it
// would always look unreachable, so we skip it there and confirm with the user.
const probeCameraReachable = async (url: string, timeoutMs = 2500) => {
  try {
    await fetch(url, { mode: "no-cors", signal: AbortSignal.timeout(timeoutMs) });
    return true;
  } catch {
    return false;
  }
};

const VPN_CONFIRMED_KEY = "bwi.cameraUi.vpnConfirmed";

const normalizeBrand = (brand: "axis" | "bwi", camera: Camera): Camera => ({
  ...camera,
  brand,
  capture_protocol: brand === "axis" ? "http" : "mqtt",
});

const SystemHardware: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));
  const accent = theme.palette.status.accent;
  const { selectedSystem } = useSession();
  const { formData, saveRawSystem } = useSystemsContext();
  const { listCamerasApi, createCameraApi, updateCameraApi, batchUpdateCamerasApi, deleteCameraApi } = useCameraApi();

  const [activeTab, setActiveTab] = useState<HardwareTab>("cameras");
  const [cameraView, setCameraView] = useState<CameraView>(CAMERA_TYPES.EXTERIOR);
  const [cameras, setCameras] = useState<Camera[]>([]);
  const [selectedCameraId, setSelectedCameraId] = useState<string | null>(null);
  const [draft, setDraft] = useState<Camera | null>(null);
  const [mode, setMode] = useState<CameraMode>("edit");
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [snackbar, setSnackbar] = useState(EMPTY_SNACK);
  const [vpnPrompt, setVpnPrompt] = useState<{ camera: Camera; url: string } | null>(null);

  const systemCode = selectedSystem?.systemCode;

  const filteredCameras = useMemo(
    () => sortCameras(cameras.filter((camera) => isCameraInView(camera, cameraView))),
    [cameraView, cameras]
  );

  const selectedCamera = useMemo(
    () => cameras.find((camera) => getCameraId(camera) === selectedCameraId) ?? null,
    [cameras, selectedCameraId]
  );

  const configEntries = useMemo(
    () => Object.entries(formData?.configurations || {}),
    [formData]
  );

  const assignedConfigNames = useMemo(
    () => (draft ? getAssignedConfigNames(draft, formData?.configurations || {}) : []),
    [draft, formData]
  );

  // Set of cameraIds that appear in at least one configuration. Used to flag
  // unassigned cameras with a red icon in the list — no click required.
  const cameraIdsWithConfigs = useMemo(() => {
    const assigned = new Set<string>();
    Object.values(formData?.configurations || {}).forEach((config: any) => {
      const cameraIds = config?.clientProcess?.cameras ?? config?.cameras ?? [];
      cameraIds.forEach((id: any) => assigned.add(String(id)));
    });
    return assigned;
  }, [formData]);

  // Tags already used by other exterior cameras — hide them from the picker
  // (except Unassigned, which can repeat, and the draft's own current tag).
  const tagsInUseByOthers = useMemo(() => {
    if (!draft) return new Set<string>();
    const draftId = getCameraId(draft);
    const taken = new Set<string>();
    cameras.forEach((camera) => {
      if (camera.type !== CAMERA_TYPES.EXTERIOR) return;
      if (getCameraId(camera) === draftId) return;
      const tag = camera.tag;
      if (tag && tag !== CameraTags.Unassigned) taken.add(tag);
    });
    return taken;
  }, [cameras, draft]);

  const loadCameras = useCallback(async () => {
    if (!systemCode) {
      setCameras([]);
      setSelectedCameraId(null);
      setDraft(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    try {
      const result = await listCamerasApi(systemCode);
      const normalized = normalizeExteriorPositions(result.map(normalizeCamera));
      setCameras(normalized);

      const nextSelected = normalized.find((camera) => isCameraInView(camera, cameraView));
      setSelectedCameraId((current) => {
        if (current && normalized.some((camera) => getCameraId(camera) === current)) {
          return current;
        }
        return nextSelected ? getCameraId(nextSelected) : null;
      });
    } catch (error) {
      console.error("Failed to load cameras:", error);
      setSnackbar({
        open: true,
        message: "Unable to load cameras for this system.",
        severity: "error",
      });
    } finally {
      setIsLoading(false);
    }
  }, [cameraView, listCamerasApi, systemCode]);

  useEffect(() => {
    loadCameras();
  }, [loadCameras]);

  useEffect(() => {
    if (!selectedCamera || mode === "add") {
      return;
    }

    setDraft(normalizeCamera(selectedCamera));
  }, [mode, selectedCamera]);

  useEffect(() => {
    if (mode === "add") {
      return;
    }

    if (filteredCameras.length === 0) {
      setSelectedCameraId(null);
      setDraft(null);
      return;
    }

    if (selectedCameraId && filteredCameras.some((camera) => getCameraId(camera) === selectedCameraId)) {
      return;
    }

    setSelectedCameraId(getCameraId(filteredCameras[0]));
    setMode("edit");
  }, [filteredCameras, mode, selectedCameraId]);

  const handleDraftChange = <K extends keyof Camera>(key: K, value: Camera[K]) => {
    setDraft((current) => {
      if (!current) return current;
      const next = { ...current, [key]: value };
      if (key === "username" && next.type === CAMERA_TYPES.INTERIOR) {
        next.password = deriveThetaPassword(value as unknown as string);
      }
      return next;
    });
  };

  const handleTypeChange = (_: React.MouseEvent<HTMLElement>, nextView: CameraView | null) => {
    if (!nextView) return;
    setCameraView(nextView);
    setMode("edit");
    setDraft(null);
  };

  const handleSelectCamera = (camera: Camera) => {
    setSelectedCameraId(getCameraId(camera));
    setMode("edit");
    setDraft(normalizeCamera(camera));
  };

  const handleAddCamera = () => {
    setMode("add");
    setSelectedCameraId(null);
    setDraft(createDraftCamera(cameraView, cameras));
  };

  const persistExteriorOrder = async (nextCameras: Camera[]) => {
    const normalizedExterior = normalizeExteriorPositions(nextCameras).filter(
      (camera) => camera.type === CAMERA_TYPES.EXTERIOR
    );

    await batchUpdateCamerasApi(normalizedExterior, systemCode!);
  };

  const handleSave = async () => {
    if (!draft || !systemCode) {
      return;
    }

    const cameraToSave = normalizeCamera({
      ...draft,
      is_under_carriage:
        draft.type === CAMERA_TYPES.EXTERIOR ? cameraView === "undercarriage" : false,
      ...(draft.type === CAMERA_TYPES.INTERIOR
        ? { password: deriveThetaPassword(draft.username) }
        : {}),
    });
    const existing = cameras.find((camera) => getCameraId(camera) === getCameraId(cameraToSave));

    // Renumber all exterior cameras from their tags so the new/edited camera
    // and its peers all carry the correct CCW-derived camera_position.
    const nextCameras = normalizeExteriorPositions([
      ...cameras.filter((camera) => getCameraId(camera) !== getCameraId(cameraToSave)),
      cameraToSave,
    ]);
    const savedCameraNormalized =
      nextCameras.find((camera) => getCameraId(camera) === getCameraId(cameraToSave)) ?? cameraToSave;

    setIsSaving(true);

    try {
      if (savedCameraNormalized.type === CAMERA_TYPES.EXTERIOR) {
        if (mode === "add" || !existing) {
          await createCameraApi(savedCameraNormalized, systemCode);
          const otherExterior = nextCameras.filter(
            (camera) =>
              camera.type === CAMERA_TYPES.EXTERIOR &&
              getCameraId(camera) !== getCameraId(savedCameraNormalized)
          );
          if (otherExterior.length > 0) {
            await batchUpdateCamerasApi(otherExterior, systemCode);
          }
        } else {
          // Always batch-update all exterior cameras on edit so position
          // numbers stay consistent if the saved camera's tag shifted ranks.
          const normalizedExterior = nextCameras.filter((camera) => camera.type === CAMERA_TYPES.EXTERIOR);
          await batchUpdateCamerasApi(normalizedExterior, systemCode);
        }
      } else if (existing) {
        await updateCameraApi(savedCameraNormalized, systemCode);
      } else {
        await createCameraApi(savedCameraNormalized, systemCode);
      }

      await loadCameras();
      setMode("edit");
      setSelectedCameraId(getCameraId(savedCameraNormalized));
      setSnackbar({
        open: true,
        message: existing ? "Camera updated." : "Camera created.",
        severity: "success",
      });
    } catch (error) {
      console.error("Failed to save camera:", error);
      setSnackbar({
        open: true,
        message: "Unable to save camera changes.",
        severity: "error",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!draft || !systemCode) {
      return;
    }

    const cameraId = draft.cameraId ?? draft.id;
    if (!cameraId) {
      return;
    }

    setIsSaving(true);

    try {
      const remaining = normalizeExteriorPositions(
        cameras.filter((camera) => getCameraId(camera) !== getCameraId(draft))
      );

      await deleteCameraApi(cameraId, systemCode);

      if (draft.type === CAMERA_TYPES.EXTERIOR && remaining.some((camera) => camera.type === CAMERA_TYPES.EXTERIOR)) {
        await persistExteriorOrder(remaining);
      }

      await loadCameras();
      setDraft(null);
      setSelectedCameraId(null);
      setMode("edit");
      setSnackbar({
        open: true,
        message: "Camera deleted.",
        severity: "success",
      });
    } catch (error) {
      console.error("Failed to delete camera:", error);
      setSnackbar({
        open: true,
        message: "Unable to delete camera.",
        severity: "error",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    if (mode === "add") {
      setDraft(selectedCamera ? normalizeCamera(selectedCamera) : null);
      setMode("edit");
      return;
    }

    setDraft(selectedCamera ? normalizeCamera(selectedCamera) : null);
  };

  const handleOpenCameraUi = async (camera: Camera) => {
    const url = getCameraUiUrl(camera, systemCode);
    if (!url) return;

    // Already confirmed on the VPN this session — just open.
    if (sessionStorage.getItem(VPN_CONFIRMED_KEY) === "1") {
      openCameraUiTab(url);
      return;
    }

    // When the app is served over http we can actually verify the camera is
    // reachable (i.e. the VPN is up) before opening.
    if (window.location.protocol === "http:") {
      // Open the tab synchronously so it stays tied to the click (avoids popup
      // blocking), then navigate it once reachability is confirmed.
      const tab = window.open("", "_blank");
      if (tab) tab.opener = null;
      const reachable = await probeCameraReachable(url);
      if (reachable) {
        sessionStorage.setItem(VPN_CONFIRMED_KEY, "1");
        if (tab) tab.location.href = url;
        else openCameraUiTab(url);
        return;
      }
      tab?.close();
    }

    // Couldn't verify (https app, or the probe failed) — ask the operator to
    // confirm the VPN is connected before opening.
    setVpnPrompt({ camera, url });
  };

  const handleUpdateAllCameraBrands = async (brand: "axis" | "bwi") => {
    if (!systemCode || cameras.length === 0) {
      return;
    }

    setIsSaving(true);

    try {
      await batchUpdateCamerasApi(cameras.map((camera) => normalizeBrand(brand, camera)), systemCode);
      await loadCameras();
      setSnackbar({
        open: true,
        message: `Updated all cameras to ${brand === "axis" ? "Axis" : "BWI"}.`,
        severity: "success",
      });
    } catch (error) {
      console.error("Failed to update all camera brands:", error);
      setSnackbar({
        open: true,
        message: "Unable to update all camera brands.",
        severity: "error",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleConfigAssignmentChange = async (configName: string, checked: boolean) => {
    if (!draft || !formData?.systemCode) {
      return;
    }

    const cameraId = getCameraId(draft);
    if (!cameraId) {
      return;
    }

    const updatedSystem = structuredClone(formData);
    const config = updatedSystem.configurations?.[configName];
    if (!config) {
      return;
    }

    const currentIds = Array.isArray(config?.clientProcess?.cameras)
      ? config.clientProcess.cameras
      : Array.isArray(config?.cameras)
        ? config.cameras
        : [];

    const nextIds = checked
      ? Array.from(new Set([...currentIds.map(String), cameraId]))
      : currentIds.map(String).filter((id: string) => id !== cameraId);

    config.clientProcess = {
      ...(config.clientProcess || {}),
      cameras: nextIds,
    };
    config.cameras = nextIds;

    setIsSaving(true);

    try {
      await saveRawSystem(updatedSystem);
      setSnackbar({
        open: true,
        message: `Updated camera assignments for ${configName}.`,
        severity: "success",
      });
    } catch (error) {
      console.error("Failed to update camera assignments:", error);
      setSnackbar({
        open: true,
        message: "Unable to update configuration assignments.",
        severity: "error",
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (!selectedSystem) {
    return (
      <Typography variant="body1" sx={{ mt: 4 }}>
        Select a system to manage hardware.
      </Typography>
    );
  }

  const isDark = theme.palette.mode === "dark";

  const surfaceCardSx = {
    borderRadius: "16px",
    border: `1px solid ${alpha(isDark ? "#fff" : "#000", isDark ? 0.06 : 0.08)}`,
    backgroundColor: isDark ? "#141416" : "#fff",
    backgroundImage: isDark
      ? `linear-gradient(180deg, ${alpha("#fff", 0.025)} 0%, ${alpha("#fff", 0)} 38%)`
      : "none",
    boxShadow: isDark
      ? "0 1px 0 rgba(255,255,255,0.04) inset, 0 12px 32px -16px rgba(0,0,0,0.6)"
      : "0 1px 2px rgba(15,23,42,0.04), 0 12px 32px -20px rgba(15,23,42,0.12)",
  } as const;

  const eyebrowSx = {
    fontSize: "11px",
    fontWeight: 600,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    color: "text.secondary",
    display: "block",
  } as const;

  const inputFieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "10px",
      backgroundColor: isDark ? alpha("#fff", 0.025) : alpha("#000", 0.015),
      transition: "box-shadow 140ms ease, background-color 140ms ease",
      "&:hover": {
        backgroundColor: isDark ? alpha("#fff", 0.04) : alpha("#000", 0.025),
      },
      "&.Mui-focused": {
        backgroundColor: isDark ? alpha("#fff", 0.04) : "#fff",
        boxShadow: `0 0 0 3px ${alpha(accent, 0.18)}`,
      },
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: alpha(isDark ? "#fff" : "#000", 0.12),
      },
      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: alpha(isDark ? "#fff" : "#000", 0.22),
      },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: accent,
        borderWidth: 1,
      },
    },
  } as const;

  const primaryButtonSx = {
    backgroundColor: accent,
    color: "#fff",
    fontWeight: 600,
    textTransform: "none",
    borderRadius: "10px",
    px: 2.25,
    py: 0.9,
    boxShadow: `0 1px 0 ${alpha("#fff", 0.06)} inset, 0 6px 16px -8px ${alpha(accent, 0.6)}`,
    transition: "box-shadow 160ms ease",
    "&:hover": {
      backgroundColor: accent,
      boxShadow: `0 0 0 4px ${alpha(accent, 0.18)}, 0 8px 24px -8px ${alpha(accent, 0.7)}`,
    },
    "&:focus-visible": {
      outline: "none",
      boxShadow: `0 0 0 4px ${alpha(accent, 0.32)}`,
    },
    "&.Mui-disabled": {
      backgroundColor: alpha(accent, 0.5),
      color: alpha("#fff", 0.7),
    },
  } as const;

  const ghostButtonSx = {
    textTransform: "none",
    borderRadius: "10px",
    fontWeight: 500,
    color: "text.secondary",
    border: `1px solid ${alpha(isDark ? "#fff" : "#000", 0.1)}`,
    px: 2,
    "&:hover": {
      color: "text.primary",
      borderColor: alpha(isDark ? "#fff" : "#000", 0.2),
      backgroundColor: alpha(isDark ? "#fff" : "#000", 0.04),
    },
  } as const;

  const sectionDividerSx = {
    borderColor: alpha(isDark ? "#fff" : "#000", 0.06),
    my: 0.5,
  } as const;

  return (
    <>
      <Box
        sx={{
          position: "relative",
          backgroundColor: isDark ? "#0b0b0d" : "#f4f4f6",
          backgroundImage: isDark
            ? `radial-gradient(900px circle at 10% -10%, ${alpha("#fff", 0.04)} 0%, transparent 55%),
               radial-gradient(700px circle at 95% 100%, ${alpha(accent, 0.05)} 0%, transparent 60%)`
            : `radial-gradient(900px circle at 10% -10%, ${alpha("#000", 0.03)} 0%, transparent 55%),
               radial-gradient(700px circle at 95% 100%, ${alpha(accent, 0.04)} 0%, transparent 60%)`,
          mx: { xs: -2, md: -3 },
          mt: { xs: -2, md: -3 },
          px: { xs: 2, md: 4 },
          pt: { xs: 3, md: 4 },
          pb: { xs: 4, md: 5 },
          minHeight: "calc(100% + 24px)",
        }}
      >
        <Stack spacing={{ xs: 3, md: 4 }} sx={{ maxWidth: 1320, mx: "auto" }}>
          {/* Page header */}
          <Stack spacing={1}>
            <Typography sx={eyebrowSx}>System hardware</Typography>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 600,
                letterSpacing: "-0.01em",
                fontSize: { xs: "1.55rem", md: "1.85rem" },
              }}
            >
              Cameras &amp; devices
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary", maxWidth: 640 }}>
              Manage physical devices tied to {selectedSystem.commonName}. Cameras are live now; other
              hardware will slot into this view as it comes online.
            </Typography>
          </Stack>

          {/* Tabs surface */}
          <Box sx={surfaceCardSx}>
            <Box
              sx={{
                px: { xs: 2, md: 3 },
                pt: 1.5,
                borderBottom: `1px solid ${alpha(isDark ? "#fff" : "#000", 0.06)}`,
              }}
            >
              <Tabs
                value={activeTab}
                onChange={(_, next) => setActiveTab(next)}
                sx={{
                  minHeight: 0,
                  "& .MuiTabs-indicator": {
                    backgroundColor: accent,
                    height: 2,
                    borderRadius: 999,
                  },
                  "& .MuiTab-root": {
                    minHeight: 44,
                    textTransform: "none",
                    fontWeight: 500,
                    color: "text.secondary",
                    px: 1.5,
                    "&.Mui-selected": {
                      color: "text.primary",
                      fontWeight: 600,
                    },
                  },
                }}
              >
                <Tab
                  data-cy="hw-cameras-tab"
                  value="cameras"
                  label="Cameras"
                  icon={<CameraAltRoundedIcon fontSize="small" />}
                  iconPosition="start"
                />
              </Tabs>
            </Box>

            {activeTab === "cameras" && (
              <Box sx={{ p: { xs: 2, md: 3 } }}>
                <Stack
                  direction={{ xs: "column", lg: "row" }}
                  spacing={2}
                  alignItems={{ xs: "stretch", lg: "center" }}
                  justifyContent="space-between"
                  sx={{ mb: 3 }}
                >
                  <ToggleButtonGroup
                    data-cy="hw-camera-view-toggle"
                    exclusive
                    value={cameraView}
                    onChange={handleTypeChange}
                    sx={{
                      backgroundColor: alpha(isDark ? "#fff" : "#000", 0.03),
                      borderRadius: "10px",
                      p: 0.5,
                      gap: 0.5,
                      "& .MuiToggleButton-root": {
                        textTransform: "none",
                        fontWeight: 500,
                        border: "none",
                        borderRadius: "8px !important",
                        px: 1.75,
                        py: 0.75,
                        color: "text.secondary",
                        "&:hover": {
                          backgroundColor: alpha(isDark ? "#fff" : "#000", 0.04),
                          color: "text.primary",
                        },
                        "&.Mui-selected": {
                          backgroundColor: isDark ? "#1d1d20" : "#fff",
                          color: "text.primary",
                          boxShadow: isDark
                            ? `0 1px 0 ${alpha("#fff", 0.08)} inset, 0 1px 3px rgba(0,0,0,0.4)`
                            : "0 1px 2px rgba(15,23,42,0.08), 0 0 0 1px rgba(15,23,42,0.06)",
                          "&:hover": {
                            backgroundColor: isDark ? "#23232a" : "#fff",
                          },
                        },
                      },
                    }}
                  >
                    <ToggleButton value={CAMERA_TYPES.EXTERIOR}>Exterior</ToggleButton>
                    <ToggleButton value="undercarriage">Undercarriage</ToggleButton>
                    <ToggleButton value={CAMERA_TYPES.INTERIOR}>Interior</ToggleButton>
                  </ToggleButtonGroup>

                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={1.25}
                    alignItems={{ xs: "stretch", sm: "center" }}
                  >
                    <Chip
                      label={`${filteredCameras.length} ${getCameraViewLabel(cameraView)} cameras`}
                      sx={{
                        borderRadius: "999px",
                        height: 28,
                        fontWeight: 500,
                        backgroundColor: alpha(isDark ? "#fff" : "#000", 0.05),
                        color: "text.secondary",
                        border: `1px solid ${alpha(isDark ? "#fff" : "#000", 0.06)}`,
                      }}
                    />
                    <ButtonGroup
                      size="small"
                      variant="outlined"
                      disabled={isSaving || isLoading || cameras.length === 0}
                      sx={{
                        "& .MuiButton-root": {
                          textTransform: "none",
                          fontWeight: 500,
                          borderColor: alpha(isDark ? "#fff" : "#000", 0.12),
                          color: "text.secondary",
                          px: 1.5,
                          "&:hover": {
                            borderColor: alpha(accent, 0.5),
                            color: accent,
                            backgroundColor: alpha(accent, 0.06),
                          },
                        },
                        "& .MuiButton-root:first-of-type": {
                          borderTopLeftRadius: "10px",
                          borderBottomLeftRadius: "10px",
                        },
                        "& .MuiButton-root:last-of-type": {
                          borderTopRightRadius: "10px",
                          borderBottomRightRadius: "10px",
                        },
                      }}
                    >
                      <Button data-cy="hw-all-axis-btn" onClick={() => handleUpdateAllCameraBrands("axis")}>All Axis</Button>
                      <Button data-cy="hw-all-bwi-btn" onClick={() => handleUpdateAllCameraBrands("bwi")}>All BWI</Button>
                    </ButtonGroup>
                    <Button
                      data-cy="hw-add-camera-btn"
                      variant="contained"
                      startIcon={<AddCircleRoundedIcon />}
                      onClick={handleAddCamera}
                      sx={primaryButtonSx}
                    >
                      Add camera
                    </Button>
                  </Stack>
                </Stack>

                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: isMobile ? "1fr" : "minmax(320px, 400px) minmax(0, 1fr)",
                    gap: { xs: 2, md: 3 },
                    alignItems: "start",
                  }}
                >
                  {/* Camera list */}
                  <Box sx={{ ...surfaceCardSx, p: 1.25, minHeight: 540 }}>
                    {isLoading ? (
                      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                        <CircularProgress sx={{ color: accent }} />
                      </Box>
                    ) : filteredCameras.length === 0 ? (
                      <Box
                        sx={{
                          minHeight: 460,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          textAlign: "center",
                          px: 3,
                        }}
                      >
                        <Box>
                          <Box
                            sx={{
                              width: 56,
                              height: 56,
                              borderRadius: "50%",
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                              backgroundColor: alpha(accent, isDark ? 0.12 : 0.08),
                              color: accent,
                              mb: 2,
                            }}
                          >
                            <CameraAltRoundedIcon sx={{ fontSize: 28 }} />
                          </Box>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                            No {getCameraViewLabel(cameraView).toLowerCase()} cameras yet
                          </Typography>
                          <Typography variant="body2" sx={{ color: "text.secondary", maxWidth: 320, mx: "auto" }}>
                            Add the first camera for this system to start organizing hardware in UMS.
                          </Typography>
                        </Box>
                      </Box>
                    ) : (
                      <Stack spacing={0.75}>
                        {filteredCameras.map((camera) => {
                          const isSelected =
                            mode === "edit" && getCameraId(camera) === getCameraId(selectedCamera ?? ({} as Camera));
                          const hasFeed = Boolean(camera.ip_address);
                          const isAssignedToConfig = cameraIdsWithConfigs.has(getCameraId(camera));
                          const iconColor = !isAssignedToConfig
                            ? theme.palette.status.error
                            : hasFeed
                              ? theme.palette.success.main
                              : theme.palette.text.disabled;
                          const iconTooltip = !isAssignedToConfig
                            ? "Not assigned to any configuration"
                            : hasFeed
                              ? ""
                              : "No IP address yet";

                          return (
                            <Box
                              data-cy={`hw-camera-card-${getCameraId(camera)}`}
                              key={getCameraId(camera)}
                              onClick={() => handleSelectCamera(camera)}
                              sx={{
                                position: "relative",
                                cursor: "pointer",
                                borderRadius: "12px",
                                border: `1px solid ${
                                  isSelected
                                    ? alpha(accent, isDark ? 0.55 : 0.4)
                                    : alpha(isDark ? "#fff" : "#000", 0.06)
                                }`,
                                backgroundColor: isSelected
                                  ? alpha(accent, isDark ? 0.1 : 0.05)
                                  : "transparent",
                                transition: "background-color 140ms ease, border-color 140ms ease, transform 140ms ease",
                                "&:hover": {
                                  backgroundColor: isSelected
                                    ? alpha(accent, isDark ? 0.12 : 0.07)
                                    : alpha(isDark ? "#fff" : "#000", 0.035),
                                  borderColor: isSelected
                                    ? alpha(accent, isDark ? 0.65 : 0.5)
                                    : alpha(isDark ? "#fff" : "#000", 0.12),
                                },
                                "&::before": isSelected
                                  ? {
                                      content: '""',
                                      position: "absolute",
                                      left: 0,
                                      top: 10,
                                      bottom: 10,
                                      width: 3,
                                      borderRadius: "0 3px 3px 0",
                                      backgroundColor: accent,
                                    }
                                  : undefined,
                                display: "grid",
                                gridTemplateColumns: "44px minmax(0, 1fr) auto",
                                gap: 1.5,
                                alignItems: "center",
                                p: 1.5,
                              }}
                            >
                              <Tooltip title={iconTooltip} arrow disableHoverListener={!iconTooltip}>
                                <Box
                                  sx={{
                                    width: 36,
                                    height: 36,
                                    borderRadius: "10px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    backgroundColor: alpha(iconColor, isDark ? 0.14 : 0.1),
                                    color: iconColor,
                                  }}
                                >
                                  {hasFeed ? (
                                    <VideocamRoundedIcon sx={{ fontSize: 20 }} />
                                  ) : (
                                    <VideocamOffRoundedIcon sx={{ fontSize: 20 }} />
                                  )}
                                </Box>
                              </Tooltip>

                              <Box sx={{ minWidth: 0 }}>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontWeight: 600,
                                    color: "text.primary",
                                    lineHeight: 1.3,
                                  }}
                                >
                                  {camera.type === CAMERA_TYPES.EXTERIOR
                                    ? camera.is_under_carriage
                                      ? `Undercarriage ${camera.camera_position}`
                                      : `Camera ${camera.camera_position}`
                                    : camera.name || camera.username || "Interior camera"}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  sx={{ color: "text.secondary", display: "block", lineHeight: 1.4 }}
                                  noWrap
                                >
                                  {cameraSubtitle(camera)}
                                </Typography>
                              </Box>

                              {isCameraUiAvailable(camera) && (
                                <Tooltip title="Open the camera's web UI in a new tab (requires VPN)" arrow>
                                  <Button
                                    data-cy={`hw-camera-ui-${getCameraId(camera)}`}
                                    size="small"
                                    startIcon={<OpenInNewRoundedIcon sx={{ fontSize: 16 }} />}
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      handleOpenCameraUi(camera);
                                    }}
                                    sx={{
                                      minWidth: 0,
                                      textTransform: "none",
                                      fontWeight: 500,
                                      borderRadius: "8px",
                                      color: "text.secondary",
                                      px: 1.25,
                                      py: 0.5,
                                      border: `1px solid ${alpha(isDark ? "#fff" : "#000", 0.1)}`,
                                      "&:hover": {
                                        color: accent,
                                        borderColor: alpha(accent, 0.5),
                                        backgroundColor: alpha(accent, 0.08),
                                      },
                                    }}
                                  >
                                    Camera UI
                                  </Button>
                                </Tooltip>
                              )}
                            </Box>
                          );
                        })}
                      </Stack>
                    )}
                  </Box>

                  {/* Edit form */}
                  <Box sx={{ ...surfaceCardSx, p: { xs: 2.5, md: 3.5 }, minHeight: 540 }}>
                    {!draft ? (
                      <Box
                        sx={{
                          minHeight: 460,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          textAlign: "center",
                          px: 3,
                        }}
                      >
                        <Box>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                            Select a camera
                          </Typography>
                          <Typography variant="body2" sx={{ color: "text.secondary", maxWidth: 360 }}>
                            Pick an existing camera from the list or add a new one to edit its hardware details.
                          </Typography>
                        </Box>
                      </Box>
                    ) : (
                      <Stack spacing={3.5}>
                        {/* Form header */}
                        <Stack spacing={0.75}>
                          <Typography sx={eyebrowSx}>
                            {mode === "add" ? "New camera" : "Editing camera"}
                          </Typography>
                          <Typography
                            variant="h6"
                            sx={{ fontWeight: 600, letterSpacing: "-0.005em" }}
                          >
                            {mode === "add"
                              ? draft.is_under_carriage
                                ? "Add undercarriage camera"
                                : "Add camera"
                              : draft.is_under_carriage
                                ? "Edit undercarriage camera"
                                : "Edit camera"}
                          </Typography>
                          <Typography variant="body2" sx={{ color: "text.secondary" }}>
                            {draft.type === CAMERA_TYPES.EXTERIOR
                              ? draft.is_under_carriage
                                ? "Undercarriage cameras use the exterior camera schema and stay grouped under their own hardware view."
                                : "Exterior cameras are position-based and are re-numbered automatically when you change order."
                              : "Interior cameras can be stored without an exterior tag or position."}
                          </Typography>
                        </Stack>

                        {/* Connection */}
                        <Stack spacing={1.5}>
                          <Typography sx={eyebrowSx}>Connection</Typography>
                          <TextField
                            data-cy="hw-camera-ip-input"
                            label="IP address"
                            value={draft.ip_address}
                            onChange={(event) => handleDraftChange("ip_address", event.target.value)}
                            fullWidth
                            sx={inputFieldSx}
                            helperText={
                              draft.type === CAMERA_TYPES.EXTERIOR
                                ? "Camera position number is assigned automatically from the tag (CCW order)."
                                : undefined
                            }
                          />
                        </Stack>

                        <Divider sx={sectionDividerSx} />

                        {/* Camera metadata */}
                        <Stack spacing={1.5}>
                          <Typography sx={eyebrowSx}>Camera details</Typography>
                          <Box
                            sx={{
                              display: "grid",
                              gridTemplateColumns:
                                draft.type === CAMERA_TYPES.EXTERIOR
                                  ? { xs: "1fr", sm: "1fr 1fr" }
                                  : "1fr",
                              gap: 2,
                            }}
                          >
                            {draft.type === CAMERA_TYPES.EXTERIOR && (
                              <TextField
                                data-cy="hw-camera-tag-select"
                                select
                                label="Camera tag"
                                value={draft.tag}
                                onChange={(event) => handleDraftChange("tag", event.target.value)}
                                fullWidth
                                sx={inputFieldSx}
                              >
                                {SORTED_CAMERA_TAGS
                                  .filter(
                                    (tag) =>
                                      tag === CameraTags.Unassigned ||
                                      tag === draft.tag ||
                                      !tagsInUseByOthers.has(tag)
                                  )
                                  .map((tag) => (
                                    <MenuItem key={tag} value={tag}>
                                      {tag}
                                    </MenuItem>
                                  ))}
                              </TextField>
                            )}

                            <TextField
                              data-cy="hw-camera-brand-select"
                              select
                              label="Brand"
                              value={draft.brand || ""}
                              onChange={(event) => handleDraftChange("brand", event.target.value)}
                              fullWidth
                              sx={inputFieldSx}
                            >
                              <MenuItem value="axis">Axis</MenuItem>
                              <MenuItem value="bwi">BWI</MenuItem>
                              <MenuItem value="theta">Theta</MenuItem>
                            </TextField>
                          </Box>

                          {draft.brand?.toLowerCase() === "theta" && (
                            <Box
                              sx={{
                                display: "grid",
                                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                                gap: 2,
                              }}
                            >
                              <TextField
                                data-cy="hw-camera-username-input"
                                label="Username (Theta serial)"
                                value={draft.username || ""}
                                onChange={(event) => handleDraftChange("username", event.target.value)}
                                helperText={
                                  draft.username && !THETA_USERNAME_PATTERN.test(draft.username.trim())
                                    ? "Expected format: THETAXX12345678. Password is auto-derived from the digits."
                                    : "Format THETAXX12345678 — password auto-fills from digits."
                                }
                                error={
                                  Boolean(draft.username) &&
                                  !THETA_USERNAME_PATTERN.test((draft.username || "").trim())
                                }
                                fullWidth
                                sx={inputFieldSx}
                              />

                              <TextField
                                data-cy="hw-camera-password-input"
                                label="Password (auto)"
                                value={deriveThetaPassword(draft.username)}
                                InputProps={{ readOnly: true }}
                                fullWidth
                                sx={inputFieldSx}
                              />
                            </Box>
                          )}

                          <TextField
                            data-cy="hw-camera-name-input"
                            label="Name"
                            value={draft.name || ""}
                            onChange={(event) => handleDraftChange("name", event.target.value)}
                            fullWidth
                            sx={inputFieldSx}
                          />

                          <Box
                            onClick={() =>
                              handleDraftChange("is_autoscan", !draft.is_autoscan)
                            }
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1.5,
                              cursor: "pointer",
                              p: 1.5,
                              borderRadius: "10px",
                              border: `1px solid ${alpha(isDark ? "#fff" : "#000", 0.08)}`,
                              backgroundColor: draft.is_autoscan
                                ? alpha(accent, isDark ? 0.1 : 0.06)
                                : "transparent",
                              transition: "background-color 140ms ease, border-color 140ms ease",
                              "&:hover": {
                                borderColor: alpha(isDark ? "#fff" : "#000", 0.18),
                              },
                            }}
                          >
                            <Checkbox
                              data-cy="hw-camera-autoscan-checkbox"
                              checked={Boolean(draft.is_autoscan)}
                              onChange={() =>
                                handleDraftChange("is_autoscan", !draft.is_autoscan)
                              }
                              onClick={(e) => e.stopPropagation()}
                              sx={{ p: 0 }}
                            />
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                Barcode / autoscan reader
                              </Typography>
                              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                                Excludes this camera from normal exterior/interior capture; used only for barcode-triggered scans.
                              </Typography>
                            </Box>
                          </Box>

                          {draft.is_autoscan && (
                            <TextField
                              data-cy="hw-camera-serial-input"
                              label="Reader serial"
                              value={draft.serial || ""}
                              onChange={(event) => handleDraftChange("serial", event.target.value)}
                              helperText={
                                draft.serial
                                  ? "Entered manually; synced by bw-unit-local to the exterior camera it applies to."
                                  : "Enter the hardware serial; it will sync to the exterior camera it applies to."
                              }
                              fullWidth
                              sx={inputFieldSx}
                            />
                          )}
                        </Stack>

                        {draft.type === CAMERA_TYPES.EXTERIOR && (
                          <>
                            <Divider sx={sectionDividerSx} />
                            <Stack spacing={1.5}>
                              <Typography sx={eyebrowSx}>HTTP &amp; failover</Typography>
                              <TextField
                                data-cy="hw-camera-query-params-input"
                                label="Query parameters"
                                helperText={
                                  draft.brand?.toLowerCase() === "bwi"
                                    ? draft.ip_address
                                      ? `Failover HTTP URL: http://${draft.ip_address}/capture`
                                      : "BWI uses MQTT first; these query params apply to the HTTP failover capture endpoint."
                                    : draft.ip_address
                                      ? `Base URL: http://${draft.ip_address}/axis-cgi/jpg/image.cgi`
                                      : "Axis query parameters will append to the standard image endpoint."
                                }
                                value={draft.query_params || ""}
                                onChange={(event) => handleDraftChange("query_params", event.target.value)}
                                fullWidth
                                sx={inputFieldSx}
                              />
                            </Stack>
                          </>
                        )}

                        {draft.type === CAMERA_TYPES.INTERIOR && (
                          <>
                            <Divider sx={sectionDividerSx} />
                            <Stack spacing={1.5}>
                              <Typography sx={eyebrowSx}>Interior options</Typography>
                              <Box
                                onClick={() =>
                                  handleDraftChange(
                                    "skip_ricoh_while_charging",
                                    !draft.skip_ricoh_while_charging
                                  )
                                }
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1.5,
                                  cursor: "pointer",
                                  p: 1.5,
                                  borderRadius: "10px",
                                  border: `1px solid ${alpha(isDark ? "#fff" : "#000", 0.08)}`,
                                  backgroundColor: draft.skip_ricoh_while_charging
                                    ? alpha(accent, isDark ? 0.1 : 0.06)
                                    : "transparent",
                                  transition: "background-color 140ms ease, border-color 140ms ease",
                                  "&:hover": {
                                    borderColor: alpha(isDark ? "#fff" : "#000", 0.18),
                                  },
                                }}
                              >
                                <Checkbox
                                  data-cy="hw-camera-skip-ricoh-checkbox"
                                  checked={Boolean(draft.skip_ricoh_while_charging)}
                                  onChange={() =>
                                    handleDraftChange(
                                      "skip_ricoh_while_charging",
                                      !draft.skip_ricoh_while_charging
                                    )
                                  }
                                  onClick={(e) => e.stopPropagation()}
                                  sx={{ p: 0 }}
                                />
                                <Box>
                                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                    Skip Ricoh while charging
                                  </Typography>
                                  <Typography variant="caption" sx={{ color: "text.secondary" }}>
                                    Pauses capture from this camera while the device is on power.
                                  </Typography>
                                </Box>
                              </Box>
                            </Stack>
                          </>
                        )}

                        {mode === "edit" && configEntries.length > 0 && (
                          <>
                            <Divider sx={sectionDividerSx} />
                            <Stack spacing={1.5}>
                              <Stack spacing={0.5}>
                                <Typography sx={eyebrowSx}>Configurations</Typography>
                                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                                  Assign this camera to the configurations that should use it.
                                </Typography>
                              </Stack>

                              <Box
                                sx={{
                                  display: "grid",
                                  gridTemplateColumns: {
                                    xs: "1fr",
                                    sm: "repeat(2, minmax(0, 1fr))",
                                    lg: "repeat(3, minmax(0, 1fr))",
                                  },
                                  gap: 1.25,
                                }}
                              >
                                {configEntries.map(([configName]) => {
                                  const checked = assignedConfigNames.includes(configName);

                                  return (
                                    <Box
                                      key={configName}
                                      onClick={() => {
                                        if (isSaving) return;
                                        handleConfigAssignmentChange(configName, !checked);
                                      }}
                                      sx={{
                                        cursor: isSaving ? "not-allowed" : "pointer",
                                        opacity: isSaving ? 0.7 : 1,
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1.25,
                                        px: 1.5,
                                        py: 1.25,
                                        borderRadius: "10px",
                                        border: `1px solid ${
                                          checked
                                            ? alpha(accent, isDark ? 0.45 : 0.32)
                                            : alpha(isDark ? "#fff" : "#000", 0.08)
                                        }`,
                                        backgroundColor: checked
                                          ? alpha(accent, isDark ? 0.1 : 0.06)
                                          : "transparent",
                                        transition: "background-color 140ms ease, border-color 140ms ease",
                                        "&:hover": {
                                          borderColor: checked
                                            ? alpha(accent, isDark ? 0.6 : 0.45)
                                            : alpha(isDark ? "#fff" : "#000", 0.18),
                                          backgroundColor: checked
                                            ? alpha(accent, isDark ? 0.13 : 0.08)
                                            : alpha(isDark ? "#fff" : "#000", 0.035),
                                        },
                                      }}
                                    >
                                      <Checkbox
                                        checked={checked}
                                        onChange={(event) =>
                                          handleConfigAssignmentChange(configName, event.target.checked)
                                        }
                                        onClick={(e) => e.stopPropagation()}
                                        disabled={isSaving}
                                        sx={{ p: 0 }}
                                      />
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          fontWeight: checked ? 600 : 500,
                                          color: checked ? "text.primary" : "text.secondary",
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                          whiteSpace: "nowrap",
                                          flex: 1,
                                          minWidth: 0,
                                        }}
                                      >
                                        {configName}
                                      </Typography>
                                    </Box>
                                  );
                                })}
                              </Box>
                            </Stack>
                          </>
                        )}

                        <Divider sx={sectionDividerSx} />

                        <Stack
                          direction={{ xs: "column", sm: "row" }}
                          spacing={1.5}
                          justifyContent="space-between"
                          alignItems={{ xs: "stretch", sm: "center" }}
                        >
                          <Button
                            data-cy="hw-camera-delete-btn"
                            variant="text"
                            startIcon={<DeleteOutlineRoundedIcon />}
                            onClick={handleDelete}
                            disabled={mode === "add" || isSaving}
                            sx={{
                              textTransform: "none",
                              fontWeight: 500,
                              borderRadius: "10px",
                              color: theme.palette.status.error,
                              "&:hover": {
                                backgroundColor: alpha(theme.palette.status.error, 0.08),
                              },
                              "&.Mui-disabled": {
                                color: alpha(theme.palette.status.error, 0.4),
                              },
                              alignSelf: { xs: "stretch", sm: "center" },
                            }}
                          >
                            Delete camera
                          </Button>

                          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                            <Button
                              data-cy="hw-camera-cancel-btn"
                              onClick={handleCancel}
                              disabled={isSaving}
                              sx={ghostButtonSx}
                            >
                              Cancel
                            </Button>
                            <Button
                              data-cy="hw-camera-save-btn"
                              variant="contained"
                              onClick={handleSave}
                              disabled={isSaving}
                              sx={{ ...primaryButtonSx, minWidth: 140 }}
                            >
                              {isSaving ? <CircularProgress size={20} sx={{ color: "#fff" }} /> : "Save camera"}
                            </Button>
                          </Stack>
                        </Stack>
                      </Stack>
                    )}
                  </Box>
                </Box>
              </Box>
            )}
          </Box>
        </Stack>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar(EMPTY_SNACK)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          onClose={() => setSnackbar(EMPTY_SNACK)}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Dialog
        open={Boolean(vpnPrompt)}
        onClose={() => setVpnPrompt(null)}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          elevation: 0,
          sx: {
            borderRadius: "18px",
            border: `1px solid ${alpha(isDark ? "#fff" : "#000", 0.08)}`,
            backgroundColor: isDark ? "#141416" : "#fff",
            boxShadow: isDark
              ? "0 24px 60px -12px rgba(0,0,0,0.7)"
              : "0 24px 60px -20px rgba(15,23,42,0.25)",
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 600, letterSpacing: "-0.005em", pt: 2.5, pb: 1 }}>
          Connect to the BWI VPN
        </DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ pt: 1 }}>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Camera UIs are only reachable over the BWI VPN. We couldn't confirm a
              connection to this camera — make sure OpenVPN is connected, then open
              the page.
            </Typography>
            {vpnPrompt ? (
              <TextField
                label="Camera URL"
                value={vpnPrompt.url}
                fullWidth
                InputProps={{ readOnly: true }}
                sx={inputFieldSx}
              />
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, pt: 1.5 }}>
          <Button
            data-cy="hw-camera-ui-cancel-btn"
            onClick={() => setVpnPrompt(null)}
            sx={ghostButtonSx}
          >
            Cancel
          </Button>
          <Button
            data-cy="hw-camera-ui-open-btn"
            variant="contained"
            onClick={() => {
              if (vpnPrompt) {
                sessionStorage.setItem(VPN_CONFIRMED_KEY, "1");
                openCameraUiTab(vpnPrompt.url);
              }
              setVpnPrompt(null);
            }}
          >
            I'm connected — open
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SystemHardware;
